import { useContext, useEffect, useState } from "react";
import { Navigate } from 'react-router';
import Alert from '../../components/alert'
import { AppContext } from '../../contexts';
import DetailsSections from '../../components/details-sections'
import Button from '../../components/button'
import { checkIcon } from '../../components/tracker/tracker'
import { useTranslation } from '../../hooks'
import { formatContact, getLocale, STORAGE_LOCALE_KEY } from '../../utils'
import defaultLabels from '../../default-labels'
import { mergePortalLabels } from '../../utilities/stringOverrideDevTools'
import './_orderDetails.scss'

const inProgressIcon = <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="8" cy="8" r="7" stroke="var(--info)" strokeWidth="1.5"/><circle cx="8" cy="8" r="3" fill="var(--info)"/></svg>

const openIcon = <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="8" cy="8" r="4" stroke="currentColor" strokeWidth="2"/></svg>
const acceptedIcon = <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/><path d="M8 5V8L10 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
const pendingReviewIcon = <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.5 8C1.5 8 4 3.5 8 3.5C12 3.5 14.5 8 14.5 8C14.5 8 12 12.5 8 12.5C4 12.5 1.5 8 1.5 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><circle cx="8" cy="8" r="2" fill="currentColor"/></svg>
const chevronIcon = <svg width="10" height="6" viewBox="0 0 10 6" xmlns="http://www.w3.org/2000/svg"><path d="M0 0H10L5 6Z" fill="currentColor"/></svg>

// Icon and label shown in each task's status circle, keyed by the task's statusKey
const STATUS_BADGES = {
  open: { icon: openIcon, labelKey: 'cp_status_open' },
  accepted: { icon: acceptedIcon, labelKey: 'cp_status_accepted' },
  completed: { icon: checkIcon, labelKey: 'cp_status_completed' },
  pendingReview: { icon: pendingReviewIcon, labelKey: 'cp_status_pending_review' }
};

// Backend sends "-" when a task has no appointment yet
const hasValue = (value) => !!value && value !== '-';

function OrderDetails() {
  const { tasks, workOrder, appointmentData = {}, needHelpLink, uid, languages = {}, allLabels = {}, setLabels } = useContext(AppContext);
  const { i18n } = useTranslation();
  const needsReview = tasks.some(task => task.state === 'alert');
  const [openTaskId, setOpenTaskId] = useState(() => tasks.find(task => task.state === 'inProgress')?.id);
  const [locale, setLocale] = useState(() => getLocale().locale);

  useEffect(() => {
    localStorage.setItem(STORAGE_LOCALE_KEY, locale);
  }, [locale]);

  // A refresh or direct link loses the session data held in context, so start again from login
  if (!tasks.length && !uid) return <Navigate to={{ pathname: '/', search: window.location.search }} replace />
  const mainContractor = formatContact(workOrder.contractorName, workOrder.contractorPhone);
  const helpContact = mainContractor || appointmentData.helpLine;
  const helpLink = appointmentData.needHelpLink || needHelpLink;

  const workOrderDetails = [
    { title: i18n('cp_service'), copy: workOrder.service },
    { title: i18n('cp_reference'), copy: workOrder.reference },
    { title: i18n('cp_address'), copy: workOrder.address },
    { title: i18n('cp_main_contractor'), copy: mainContractor },
    {
      title: i18n('cp_status'),
      copy: (
        <span className="order-details__status">
          {inProgressIcon}
          {workOrder.statusName}
        </span>
      )
    }
  ].filter(item => hasValue(item.copy));
  const helpPhone = mainContractor ? workOrder.contractorPhone : appointmentData.helpLine;
  const needHelp = (helpContact || helpLink) && (
    <div className="help-card">
      <h3>{i18n('cp_need_help')}</h3>
      {helpContact && (
        <p className="contact">
          {mainContractor && `${i18n('cp_contact_your_main_contractor')}: `}
          {workOrder.contractorName}
          {workOrder.contractorName && helpPhone && ' | '}
          {helpPhone && <a href={`tel:${helpPhone}`}>{helpPhone}</a>}
        </p>
      )}
      {helpLink && (
        <p className="note">
          {i18n('cp_general_questions')}{' '}
          <a href={helpLink} target="_blank" rel="noopener noreferrer">
            {i18n('cp_link')}
          </a>.
        </p>
      )}
    </div>
  );

  const getTaskDetails = (task) => [
    { title: i18n('cp_subcontractor'), copy: formatContact(task.subcontractorName, task.subcontractorPhone) },
    { title: i18n('cp_appointment_window'), copy: task.appointmentDates },
    { title: i18n('cp_technician'), copy: task.assigneeName }
  ].filter(item => hasValue(item.copy));

  const toggleTask = (id) => setOpenTaskId(current => (current === id ? undefined : id));

  const changeLocale = (e) => {
    const next = e.target.value;
    if (next === locale) return;
    setLocale(next);
    // Labels for every language are already loaded, so swap them in place instead of reloading the page
    setLabels(mergePortalLabels({ ...defaultLabels, ...(allLabels[next] || allLabels.en || allLabels) }));
  };

  return (
    <div className="container order-details">
      {needsReview && <Alert type="warning" label={i18n('cp_review_task_banner')} />}
      <div className="card order-details__card">
        {Object.keys(languages).length > 1 && (
          <div className="language-toggle-container">
            <select className="lang-select" value={locale} onChange={changeLocale}>
              {Object.entries(languages).map(([name, code]) => (
                <option key={code} value={code}>
                  {name.charAt(0).toUpperCase() + name.slice(1)}
                </option>
              ))}
            </select>
          </div>
        )}
        <DetailsSections title={i18n('cp_work_order')} items={workOrderDetails} />
      </div>
      <section className="order-details__tasks-section">
        <div className="order-details__tasks-header">
          <h2 className="order-details__section-title">{i18n('cp_tasks')}</h2>
          <p className="order-details__tasks-copy">{i18n('cp_tasks_copy')}</p>
        </div>
        <ul className="order-details__tasks">
          {tasks.map(task => {
            const isOpen = openTaskId === task.id;
            const statusKey = STATUS_BADGES[task.statusKey] ? task.statusKey : 'open';
            const badge = STATUS_BADGES[statusKey];
            return (
              <li
                className={`order-details__task order-details__task--${task.state}${isOpen ? ' order-details__task--open' : ''}`}
                key={task.id}
              >
                <div className="order-details__task-header">
                  <span
                    className={`order-details__task-status order-details__task-status--${statusKey}`}
                    role="img"
                    aria-label={i18n(badge.labelKey)}
                    title={i18n(badge.labelKey)}
                  >
                    {badge.icon}
                  </span>
                  <span className="order-details__task-labels">
                    <span className="order-details__task-title">{task.title}</span>
                    <span className="order-details__task-subtitle">
                      {i18n(task.subTitleKey)}
                    </span>
                  </span>
                  <span className="order-details__task-actions">
                    {/* Review report for tasks awaiting review, Details otherwise */}
                    <Button
                      to={task.to}
                      target="_self"
                      testId={task.state === 'alert' ? `task-${task.id}` : `task-details-${task.id}`}
                    >
                      {i18n(task.actionLabelKey)}
                    </Button>
                    <button
                      type="button"
                      className="order-details__task-toggle"
                      onClick={() => toggleTask(task.id)}
                      aria-expanded={isOpen}
                      aria-label={isOpen ? i18n('cp_close') : i18n('cp_details')}
                      data-testid={`task-toggle-${task.id}`}
                    >
                      <span className="order-details__task-chevron">{chevronIcon}</span>
                    </button>
                  </span>
                </div>
                {isOpen && (
                  <div className="order-details__task-body">
                    {task.description && (
                      <div className="order-details__task-field">
                        <span className="order-details__task-field-title">{i18n('cp_what_is_this')}</span>
                        <span>{task.description}</span>
                      </div>
                    )}
                    <div className="order-details__task-grid">
                      {getTaskDetails(task).map(item => (
                        <div className="order-details__task-field" key={item.title}>
                          <span className="order-details__task-field-title">{item.title}</span>
                          <span>{item.copy}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      </section>
      {needHelp && <footer>{needHelp}</footer>}
    </div>
  )
}

export default OrderDetails
